import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import {
  initPersistence, watchAuthState, signInWithGoogle, firebaseSignOut,
  fsGet, fsSet, fsGetCol, fsLogChange, refreshGCalToken,
} from './lib/firebase';
import { SERVER_SIDE_AUTH } from './lib/gcalAuth';
import {
  getTokenFromUrl, getSavedToken, saveTokenLocally,
  cleanTokenFromUrl, clearSavedToken, validateToken,
} from './lib/tokens';
import {
  getInviteCodeFromUrl, cleanInviteCodeFromUrl, saveInviteCodeLocally,
  getSavedInviteCode, clearSavedInviteCode, redeemInviteCode,
} from './lib/signupInvites';
import SignInScreen    from './screens/SignInScreen';
import SetupWizard     from './screens/SetupWizard';
import HomeScreen      from './screens/HomeScreen';
import SharedView      from './screens/SharedView';
import LoadingScreen   from './screens/LoadingScreen';
import InviteGateScreen from './screens/InviteGateScreen';
import VersionFooter   from './components/ui/VersionFooter';
// School module — stub for now; replaced by the real routed module once the
// screens are ported (see the "school module" build phase).
import StudentView     from './school/StudentView';

// ─── APP CONTEXT ──────────────────────────────────────────────────────────────
export const AppContext = createContext(null);
export function useApp() { return useContext(AppContext); }

// ─── ROLE CONSTANTS ───────────────────────────────────────────────────────────
// Caregivers (Google sign-in or token link) no longer carry a role — they're
// routed through SharedView with per-person scopes. Only OWNER remains.
export const ROLES = { OWNER: 'owner' };

export default function App() {
  const [appState, setAppState]       = useState('loading');
  const [currentUser, setCurrentUser] = useState(null);
  const [familyData, setFamilyData]   = useState(null);
  const [userRole, setUserRole]       = useState(null);
  const [tokenDoc, setTokenDoc]       = useState(null); // for shared/token-based views
  const [loadError, setLoadError]     = useState(null); // connectivity error during bootstrap — shown with a Retry

  // Guard: prevent watchAuthState from firing loadUserAndFamily more than once
  // during the Firebase cold-start sequence (it often fires 3-5× on first load)
  const authLoadingRef  = useRef(false);  // true while a load is in-flight
  const lastUidRef      = useRef(null);   // track last loaded UID to skip duplicates
  // Signup invite code redeemed during loadUserAndFamily, needed by
  // handleSetupComplete to stamp inviteCode onto the new family doc.
  const pendingInviteCodeRef = useRef(null);

  useEffect(() => {
    let unsub;
    initPersistence().then(async () => {

      // ── 0. Stash any invite code from the URL for loadUserAndFamily to use ──
      // Saved to localStorage (not just kept in memory) so it survives the
      // Google OAuth popup and any page reload during the setup wizard.
      const urlInvite = getInviteCodeFromUrl();
      if (urlInvite) {
        cleanInviteCodeFromUrl();
        saveInviteCodeLocally(urlInvite);
      }

      // ── 1. Check for token in URL or localStorage FIRST ──────────────────
      // If a valid token is found, skip Firebase auth entirely and show the
      // appropriate shared view. The token is saved locally so return visits
      // work without the URL parameter.
      const urlToken   = getTokenFromUrl();
      const savedToken = getSavedToken();
      const rawToken   = urlToken || savedToken;

      if (rawToken) {
        if (urlToken) cleanTokenFromUrl(); // tidy the URL immediately
        try {
          const doc = await validateToken(rawToken);
          if (doc) {
            // Valid token — hydrate family data (read-only, no auth required)
            saveTokenLocally(rawToken);
            const family = await fsGet(`families/${doc.familyId}`);
            if (family) {
              const { loadHouseData } = await import('./lib/houseData');
              const [carePartners, children, sitters, config, houseData] = await Promise.all([
                fsGetCol(`families/${doc.familyId}/carePartners`),
                fsGetCol(`families/${doc.familyId}/children`),
                fsGetCol(`families/${doc.familyId}/sitters`),
                fsGet(`families/${doc.familyId}/config/appConfig`),
                loadHouseData(doc.familyId).catch(() => ({})),
              ]);
              setFamilyData({ ...family, carePartners, children, sitters, config, houseData });
              setTokenDoc(doc);
              setAppState('shared');
              return; // don't start the Firebase auth listener
            }
          } else {
            // Token invalid or revoked — clear it and fall through to normal auth
            clearSavedToken();
          }
        } catch (err) {
          // Connectivity hiccup (e.g. a Firestore read timeout) — don't leave
          // the app stuck on the loading screen forever; fall through to the
          // normal Firebase auth flow below instead.
          console.error('[FOH] token bootstrap failed:', err);
        }
      }

      // ── 2. Normal Firebase Auth flow ─────────────────────────────────────
      unsub = watchAuthState(async (firebaseUser) => {
        if (!firebaseUser) {
          // Only reset if we were previously signed in (avoid spurious initial null fire)
          if (lastUidRef.current !== null) {
            lastUidRef.current = null;
            authLoadingRef.current = false;
            setCurrentUser(null);
            setFamilyData(null);
            setAppState('sign_in');
          } else {
            // First fire is null before Firebase restores the session — just show sign-in
            setAppState('sign_in');
          }
          return;
        }

        // Skip if this UID is already loaded or a load is in-flight
        if (authLoadingRef.current || lastUidRef.current === firebaseUser.uid) return;

        authLoadingRef.current = true;
        lastUidRef.current = firebaseUser.uid;

        const user = {
          uid:        firebaseUser.uid,
          email:      firebaseUser.email,
          name:       firebaseUser.displayName,
          given_name: firebaseUser.displayName?.split(' ')[0] || '',
          picture:    firebaseUser.photoURL,
        };
        setCurrentUser(user);
        await loadUserAndFamily(user);
        authLoadingRef.current = false;
      });
    });
    return () => unsub?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactively renew the Google Calendar access token in the background
  // while an owner session is active, so it rarely goes stale in the middle
  // of use — the reactive "Reconnect" banner (HomeScreen/LogisticsModule)
  // remains the fallback for whenever silent renewal isn't possible. Scoped
  // to appState so it starts/stops with the authenticated session; App.jsx
  // is the one component that stays mounted across every internal screen
  // (Home, Review & Assign, Settings), unlike screen-level state.
  //
  // Three triggers, not just the interval: a session left open for hours
  // hits the interval fine, but the far more common case is the token going
  // stale while the tab is backgrounded/closed — the interval alone would
  // leave that undetected until it next happens to fire while visible,
  // meaning the FIRST thing the user sees on return is the reconnect banner
  // even though a silent refresh attempted right then would likely have just
  // worked. Firing on mount and on every resume-to-visible closes that gap.
  useEffect(() => {
    if (appState !== 'home') return;
    // Server-side auth removes the reason this effect exists: the token
    // function refreshes on demand from a refresh token that doesn't go stale
    // while the tab is away, so there is nothing to pre-warm or poll for.
    if (SERVER_SIDE_AUTH) return;

    refreshGCalToken(); // catch a token that went stale while this session was away

    const REFRESH_INTERVAL_MS = 45 * 60 * 1000; // comfortably inside the 55-min staleness window
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refreshGCalToken();
    }, REFRESH_INTERVAL_MS);

    function onVisible() {
      if (document.visibilityState === 'visible') refreshGCalToken();
    }
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [appState]);

  // Firestore security rules mean a query the user doesn't match can come
  // back permission-denied rather than empty — that's expected and means
  // "try the next query shape". A timeout/network error is not — surface
  // that as a retryable error instead of silently cascading to 'setup'.
  function isConnectivityError(err) {
    return err?.message === 'Firestore read timed out' || err?.code === 'unavailable';
  }

  async function loadUserAndFamily(user) {
    setAppState('loading');
    setLoadError(null);
    const email = user.email.toLowerCase();

    try {
      try {
        const families = await fsGetCol('families', [{ field: 'creatorEmail', op: '==', value: email }]);
        if (families?.length > 0) { await hydrateFamily(families[0], user, ROLES.OWNER); return; }
      } catch (err) { if (isConnectivityError(err)) throw err; }

      try {
        const families = await fsGetCol('families', [{ field: 'allowedEmails', op: 'array-contains', value: email }]);
        if (families?.length > 0) { await hydrateFamily(families[0], user, ROLES.OWNER); return; }
      } catch (err) { if (isConnectivityError(err)) throw err; }

      // Caregiver Google sign-in — families.signInEmails is a flat array kept in
      // sync with each caregiver's email + access.googleSignInEnabled toggle
      // (see lib/tokens.js syncSignInEmail), so this is a plain array-contains
      // query; which caregiver/scopes resolves locally once sitters are loaded.
      try {
        const families = await fsGetCol('families', [{ field: 'signInEmails', op: 'array-contains', value: email }]);
        if (families?.length > 0) {
          const sitters = await fsGetCol(`families/${families[0].id}/sitters`);
          const sitter = sitters.find(s => (s.email || '').toLowerCase() === email && s.access?.googleSignInEnabled && s.active !== false);
          if (sitter) { await hydrateAsCaregiver(families[0], sitter, sitters); return; }
        }
      } catch (err) { if (isConnectivityError(err)) throw err; }

      // Homeschool student — families.homeschoolStudentEmails is a flat array
      // kept in sync with each child's schoolType + studentEmail fields (see
      // lib/tokens.js syncHomeschoolStudent), mirroring the caregiver branch above.
      try {
        const families = await fsGetCol('families', [{ field: 'homeschoolStudentEmails', op: 'array-contains', value: email }]);
        if (families?.length > 0) {
          const children = await fsGetCol(`families/${families[0].id}/children`);
          const child = children.find(c => (c.studentEmail || '').toLowerCase() === email && c.schoolType === 'homeschool');
          if (child) { await hydrateAsStudent(families[0], child); return; }
        }
      } catch (err) { if (isConnectivityError(err)) throw err; }

      // No existing family match — this is a brand-new signup, which during
      // the trial requires a redeemed invite code (see firestore.rules'
      // families/create check). Without one, don't route into SetupWizard at
      // all; a permission-denied write there would be a confusing dead end.
      const pendingCode = getSavedInviteCode();
      if (!pendingCode) {
        setAppState('invite_required');
        return;
      }

      const result = await redeemInviteCode(pendingCode, email);
      if (!result.ok) {
        clearSavedInviteCode();
        setAppState('invite_invalid');
        return;
      }

      pendingInviteCodeRef.current = pendingCode;
      setAppState('setup');
    } catch (err) {
      console.error('[FOH] loadUserAndFamily connectivity error:', err);
      lastUidRef.current = null;
      authLoadingRef.current = false;
      setLoadError('Could not reach the server. Check your connection and try again.');
    }
  }

  async function hydrateFamily(family, user, role) {
    try {
      const { loadHouseData } = await import('./lib/houseData');
      const [carePartners, children, sitters, config, houseData] = await Promise.all([
        fsGetCol(`families/${family.id}/carePartners`),
        fsGetCol(`families/${family.id}/children`),
        fsGetCol(`families/${family.id}/sitters`),
        fsGet(`families/${family.id}/config/appConfig`),
        loadHouseData(family.id, { includePrivate: role === ROLES.OWNER }),
      ]);
      setFamilyData({ ...family, carePartners, children, sitters, config, houseData });
      setUserRole(role);
      setAppState('home');
    } catch (err) {
      console.error('[FOH] hydrate failed:', err);
      // Reset refs so the same UID can be retried on next auth state change
      lastUidRef.current = null;
      authLoadingRef.current = false;
      setAppState('sign_in');
    }
  }

  // Google-signed-in caregiver — routed through the same SharedView a token
  // link uses, with a synthetic tokenDoc built from their access settings
  // instead of a real accessTokens/{token} doc.
  async function hydrateAsCaregiver(family, sitter, sitters) {
    try {
      const { loadHouseData } = await import('./lib/houseData');
      const [carePartners, children, config, houseData] = await Promise.all([
        fsGetCol(`families/${family.id}/carePartners`),
        fsGetCol(`families/${family.id}/children`),
        fsGet(`families/${family.id}/config/appConfig`),
        loadHouseData(family.id).catch(() => ({})),
      ]);
      setFamilyData({ ...family, carePartners, children, sitters, config, houseData });
      setTokenDoc({ label: sitter.firstName, scopes: sitter.access?.scopes || {}, caregiverId: sitter.id });
      setAppState('shared');
    } catch (err) {
      console.error('[FOH] hydrate caregiver failed:', err);
      lastUidRef.current = null;
      authLoadingRef.current = false;
      setAppState('sign_in');
    }
  }

  // Homeschool student — a fourth top-level appState, distinct from 'home'
  // (owner-only) and 'shared' (caregiver). Only loads this child's own
  // school-module data, not the rest of the family's Grove data.
  async function hydrateAsStudent(family, child) {
    try {
      // These two feed SchoolDataProvider's initial state, so the student's
      // first paint has data instead of a "Loading today…" flash; its
      // listeners supersede them a moment later. Nothing else reads them.
      const [courses, assignments] = await Promise.all([
        fsGetCol(`families/${family.id}/courses`),
        fsGetCol(`families/${family.id}/assignments`, [{ field: 'studentEmail', op: '==', value: child.studentEmail }]),
      ]);
      setFamilyData({ ...family, courses, assignments, activeChild: child });
      setAppState('student');
    } catch (err) {
      console.error('[FOH] hydrate student failed:', err);
      lastUidRef.current = null;
      authLoadingRef.current = false;
      setAppState('sign_in');
    }
  }

  async function handleSignIn() {
    try {
      const { user } = await signInWithGoogle();
      const mapped = {
        uid: user.uid, email: user.email, name: user.displayName,
        given_name: user.displayName?.split(' ')[0] || '', picture: user.photoURL,
      };
      setCurrentUser(mapped);
      await loadUserAndFamily(mapped);
    } catch (err) {
      console.error('[FOH] sign in error:', err);
      alert('Sign in failed. Make sure pop-ups are allowed and try again.');
    }
  }

  async function handleSignOut() {
    await firebaseSignOut();
    lastUidRef.current = null;
    authLoadingRef.current = false;
    setCurrentUser(null); setFamilyData(null); setUserRole(null);
    setAppState('sign_in');
  }

  async function handleSetupComplete(setupData) {
    if (!currentUser) return;
    // Random id — familyId is the read boundary for family data, so it must
    // not be derivable from the owner's email (owners resolve by email query)
    const { generateToken } = await import('./lib/tokens');
    const familyId = `fam_${generateToken()}`;

    await fsSet(`families/${familyId}`, {
      id: familyId,
      familyName:    setupData.familyName,
      creatorEmail:  currentUser.email.toLowerCase(),
      allowedEmails: [
        currentUser.email.toLowerCase(),
        ...(setupData.carePartners || []).map(cp => cp.email.toLowerCase()),
      ],
      signInEmails: [],
      // Required by the families/create rule's redeemed-invite cross-check.
      inviteCode: pendingInviteCodeRef.current,
    });
    clearSavedInviteCode();
    pendingInviteCodeRef.current = null;

    const creatorId = `cp_${currentUser.email.split('@')[0]}`;
    await fsSet(`families/${familyId}/carePartners/${creatorId}`, {
      id: creatorId, name: currentUser.name, email: currentUser.email,
      identifier: setupData.creatorId || currentUser.given_name?.[0]?.toUpperCase() || 'A',
      isCreator: true,
      scheduleConvention: setupData.workProfiles?.[0]?.convention || 'mixed',
    });

    for (const cp of (setupData.carePartners || [])) {
      const id = `cp_${cp.email.split('@')[0]}`;
      await fsSet(`families/${familyId}/carePartners/${id}`, { id, ...cp });
    }
    for (const child of (setupData.children || [])) {
      await fsSet(`families/${familyId}/children/${child.id}`, child);
    }
    for (const sitter of (setupData.sitters || [])) {
      await fsSet(`families/${familyId}/sitters/${sitter.id}`, sitter);
    }

    const calendarAssignments = {};
    const suppressedCalendars = [];
    for (const cal of (setupData.calendars || [])) {
      if (cal.suppress || cal.personName === 'ignore') suppressedCalendars.push(cal.calendarId);
      else if (cal.personName) calendarAssignments[cal.calendarId] = cal.personName;
    }

    await fsSet(`families/${familyId}/config/appConfig`, {
      cashOnHand: 0, cashThreshold: 50,
      defaultRatePerHr: { upTo2Kids: 25, moreThan2Kids: 30 },
      keywordRegistries: {
        auxiliaryCare: ['Back-Up Care', 'Bright Horizons', 'backup care'],
        schoolSignals: ['No School', 'Day Off', 'Holiday', 'Early Release', 'Half Day'],
      },
      calendarAssignments,
      suppressedCalendars,
      calendarMeta: (setupData.calendars || []).map(c => ({
        id: c.calendarId, name: c.calendarName, color: c.color, personName: c.personName, accessRole: c.accessRole || null,
      })),
    });

    await fsLogChange(familyId, {
      actorId: currentUser.email, actorName: currentUser.name,
      module: 'setup', action: 'create_family',
      entityType: 'family', entityId: familyId,
      fromValue: null, toValue: setupData.familyName,
    });

    await loadUserAndFamily(currentUser);
  }

  const contextValue = { currentUser, familyData, setFamilyData, userRole, tokenDoc, handleSignOut };

  return (
    <AppContext.Provider value={contextValue}>
      <div className="app-root">
        {appState === 'loading' && (
          <LoadingScreen error={loadError} onRetry={currentUser ? () => loadUserAndFamily(currentUser) : undefined} />
        )}
        {appState === 'sign_in' && <SignInScreen onSignIn={handleSignIn} />}
        {appState === 'invite_required' && <InviteGateScreen reason="missing" onSignOut={handleSignOut} />}
        {appState === 'invite_invalid'  && <InviteGateScreen reason="invalid" onSignOut={handleSignOut} />}
        {appState === 'setup'   && <SetupWizard onComplete={handleSetupComplete} />}
        {appState === 'home'    && <HomeScreen />}
        {appState === 'shared'  && <SharedView tokenDoc={tokenDoc} />}
        {appState === 'student' && <StudentView />}
        <VersionFooter />
      </div>
    </AppContext.Provider>
  );
}
